import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Loader2 } from 'lucide-react';
import { canonicalPrintGroupLabel } from '../lib/printGroups';

export default function MonitorSelect() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/pos/login?redirect=' + encodeURIComponent(window.location.pathname));
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      api.getMonitorProfiles()
        .then(setProfiles)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [isAuthenticated]);

  if (authLoading || (isAuthenticated && loading)) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center gap-6 p-8 text-center text-white">
        <div className="w-20 h-20 bg-slate-800 rounded-3xl flex items-center justify-center text-slate-500 shadow-2xl mb-4">
          <Lock className="w-10 h-10" />
        </div>
        <h1 className="text-3xl font-bold">Monitor gesperrt</h1>
        <p className="text-slate-400 max-w-sm">Bitte melden Sie sich mit Ihren Zugangsdaten an, um auf die Küchenmonitore zuzugreifen.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center gap-8 p-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-white mb-2">FiscalPOS Monitor</h1>
        <p className="text-slate-400 text-lg">Bestellmonitor auswählen</p>
      </div>

      {profiles.length === 0 ? (
        <div className="text-slate-500 text-center">
          <p>Keine Monitor-Profile gefunden.</p>
          <p className="text-sm mt-2">Erstelle Profile im Manager-Portal unter Einstellungen → Monitore.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-3xl">
          {profiles.map((profile) => (
            <button
              key={profile.id}
              onClick={() => navigate(`/monitor/${profile.id}`)}
              className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500 rounded-xl p-6 text-left transition-all group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                <span className="text-slate-400 text-xs uppercase tracking-wider">Live</span>
              </div>
              <h2 className="text-white text-xl font-semibold group-hover:text-blue-400 transition-colors">
                {profile.name}
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {(profile.print_groups || []).map((pg) => (
                  <span key={pg} className="bg-slate-700 text-slate-300 text-xs px-2 py-1 rounded-full">
                    {canonicalPrintGroupLabel(pg)}
                  </span>
                ))}
              </div>
              <div className="mt-3 text-slate-500 text-xs">
                Warnung: {profile.warn_minutes}min · Alarm: {profile.alert_minutes}min
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
